import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import PageTitle from '../../FormElements/PageTitle';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import FilterByPosition from '../../Filters/FilterByPosition';
import FilterByTeamMultiSelect from '../../Filters/FilterByTeamMultiSelect';
import FilterBySearchInput from '../../Filters/FilterBySearchInput';
import FilterByIsRookie from '../../Filters/FilterByIsRookie';
import ClearFiltersButton from '../../Filters/ClearFilterButton/ClearFiltersButton';
import PaginationTracker from '../../Pagination/PaginationTracker/PaginationTracker';
import PaginationTrackerMobile from '../../Pagination/PaginationTrackerMobile/PaginationTrackerMobile';
import PerPageDropdown from '../../Pagination/PerPageDropdown/PerPageDropdown';
import { playerAvatarImgClassName } from '../../../utils/avatarClassNames';
import {
	getCachedTeamLogoSrc,
	prefetchTeamLogos,
	subscribeTeamLogoCache,
	TEAM_LOGO_CACHE_SIZE
} from '../../../utils/teamLogoCache';

const RANKINGS_AVATAR_SIZE = 32;
const PLAYOFF_WEEKS = [15, 16, 17];

const FAVORABILITY = {
	favorable: {
		label: 'Favorable',
		dotClass: 'bg-green-500'
	},
	neutral: {
		label: 'Neutral',
		dotClass: 'bg-yellow-400'
	},
	unfavorable: {
		label: 'Unfavorable',
		dotClass: 'bg-red-500'
	}
};

const rankingsAvatarWrapperStyle = {
	width: `${RANKINGS_AVATAR_SIZE}px`,
	height: `${RANKINGS_AVATAR_SIZE}px`,
	overflow: 'hidden',
	marginRight: '8px',
	flexShrink: 0
};

const rankingsAvatarImgStyle = {
	width: `${RANKINGS_AVATAR_SIZE}px`,
	height: `${RANKINGS_AVATAR_SIZE}px`,
	minWidth: `${RANKINGS_AVATAR_SIZE}px`,
	minHeight: `${RANKINGS_AVATAR_SIZE}px`
};

const PROJECTION_EDITOR_EMAIL = 'mhoelzel23@gmail.com';
const DEFAULT_SEASON = new Date().getFullYear();
const DEFAULT_SCORING_FORMAT = 'half_ppr';
const DEFAULT_PLATFORM = 'underdog';
const PLATFORM_OPTIONS = [
	{ value: 'underdog', label: 'Underdog' },
	{ value: 'drafters', label: 'Drafters' }
];

const POSITION_BUTTONS = [
	{ label: 'All', value: 'all' },
	{ label: 'QB', value: 'quarterback' },
	{ label: 'RB', value: 'running back' },
	{ label: 'WR', value: 'wide receiver' },
	{ label: 'TE', value: 'tight end' }
];

const POSITION_FILTER_TO_ABBR = {
	quarterback: 'QB',
	'running back': 'RB',
	'wide receiver': 'WR',
	'tight end': 'TE'
};

const POSITION_ABBR_TO_PROFILE = {
	QB: 'quarterback',
	RB: 'running back',
	WR: 'wide receiver',
	TE: 'tight end'
};

function playerProfilePath(entry) {
	if (!entry.player_id) return null;

	const position =
		POSITION_ABBR_TO_PROFILE[entry.position] || entry.position;

	return `/players/${entry.player_id}?position=${encodeURIComponent(position)}`;
}

const TABLE_WRAPPER_CLASS =
	'w-full border border-gray-300 dark:border-dark-border rounded-md overflow-x-auto bg-white dark:bg-dark-card text-gray-800 dark:text-dark-text';

const TABLE_HEAD_CLASS = 'bg-gray-100 dark:bg-gray-800 sticky top-0 z-10';

const TABLE_HEAD_CELL_CLASS =
	'px-8 py-3 text-left text-gray-800 dark:text-dark-text border-b border-gray-300 dark:border-dark-border font-semibold';

const WEEK_HEAD_CELL_CLASS =
	'px-2 py-3 text-center text-gray-800 dark:text-dark-text border-b border-l border-gray-300 dark:border-dark-border font-semibold whitespace-nowrap';

const TABLE_BODY_CELL_CLASS =
	'pl-8 py-3 text-left text-gray-800 dark:text-dark-text whitespace-nowrap';

const WEEK_BODY_CELL_CLASS =
	'px-2 py-2 text-center border-l border-gray-200 dark:border-dark-border whitespace-nowrap';

const TABLE_ROW_BASE_CLASS =
	'border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card transition-colors hover:bg-gray-50 dark:hover:bg-gray-800';

function getCsrfToken() {
	return document.querySelector('[name="csrf-token"]')?.content;
}

function buildEndpoint(base, params) {
	const url = new URL(base, window.location.origin);
	Object.entries(params).forEach(([key, value]) => {
		if (value != null && value !== '') {
			url.searchParams.set(key, value);
		}
	});
	return url.toString();
}

function normalizeTeamKey(value) {
	return (value || '').toString().trim().toLowerCase();
}

function buildTeamLookup(teams) {
	const lookup = {};
	const aliases = {
		jac: 'jax',
		wsh: 'was'
	};

	teams.forEach((team) => {
		const keys = [team.name, team.abbreviation, team.location].filter(Boolean);
		keys.forEach((key) => {
			lookup[normalizeTeamKey(key)] = team;
		});
	});

	Object.entries(aliases).forEach(([alias, canonical]) => {
		if (lookup[canonical] && !lookup[alias]) {
			lookup[alias] = lookup[canonical];
		}
	});

	return lookup;
}

function getWeekMatchup(team, week) {
	if (!team?.weeks) return null;
	return team.weeks[week] || team.weeks[String(week)] || null;
}

function playerNameFavorabilityKey(entry) {
	return `name:${normalizeTeamKey(entry?.full_name)}|${normalizeTeamKey(
		entry?.position
	)}|${normalizeTeamKey(entry?.team_name)}`;
}

function playerFavorabilityKey(entry) {
	if (entry?.player_id) {
		return `id:${entry.player_type || 'Player'}:${entry.player_id}`;
	}

	return playerNameFavorabilityKey(entry);
}

function buildFavorabilityLookupFromMap(byPlayer) {
	return byPlayer && typeof byPlayer === 'object' ? { ...byPlayer } : {};
}

function lookupEntryFavorabilities(lookup, entry) {
	return (
		lookup[playerFavorabilityKey(entry)] ||
		lookup[playerNameFavorabilityKey(entry)] ||
		null
	);
}

function getWeekFavorability(favorabilities, week) {
	if (!favorabilities) return null;
	return favorabilities[week] ?? favorabilities[String(week)] ?? null;
}

function reorderEntries(entries, draggedId, targetId) {
	if (draggedId === targetId) return entries;

	const next = [...entries];
	const fromIndex = next.findIndex((entry) => entry.id === draggedId);
	const toIndex = next.findIndex((entry) => entry.id === targetId);
	if (fromIndex < 0 || toIndex < 0) return entries;

	const [moved] = next.splice(fromIndex, 1);
	next.splice(toIndex, 0, moved);

	return next.map((entry, index) => ({
		...entry,
		rank: index + 1
	}));
}

function matchesPositionFilter(entry, filterPosition) {
	const allCount = POSITION_BUTTONS.length - 1;
	if (filterPosition.length === 0 || filterPosition.length === allCount) {
		return true;
	}

	const entryFilterValue = Object.entries(POSITION_FILTER_TO_ABBR).find(
		([, abbr]) => abbr === entry.position
	)?.[0];

	return entryFilterValue ? filterPosition.includes(entryFilterValue) : false;
}

function matchesTeamFilter(entry, teamFilter) {
	if (!teamFilter) return true;

	const teams = teamFilter.split(',').filter(Boolean);
	if (teams.length === 0) return true;

	return teams.includes(entry.team_name);
}

function matchesRookieFilter(entry, isRookieFilter) {
	if (!isRookieFilter) return true;

	return entry.is_rookie === true;
}

function matchesSearchFilter(entry, search) {
	const query = search.trim().toLowerCase();
	if (!query) return true;

	return (
		entry.full_name.toLowerCase().includes(query) ||
		(entry.team_name || '').toLowerCase().includes(query)
	);
}

function filterRankingEntries(entries, filter, search) {
	return entries.filter(
		(entry) =>
			matchesPositionFilter(entry, filter.position) &&
			matchesTeamFilter(entry, filter.team) &&
			matchesRookieFilter(entry, filter.isRookie) &&
			matchesSearchFilter(entry, search)
	);
}

function PlayerAvatar({ entry }) {
	if (entry.image_url) {
		return (
			<div style={rankingsAvatarWrapperStyle}>
				<img
					className={playerAvatarImgClassName}
					src={`${entry.image_url}?w=${RANKINGS_AVATAR_SIZE}&h=${RANKINGS_AVATAR_SIZE}`}
					alt={entry.full_name}
					style={rankingsAvatarImgStyle}
				/>
			</div>
		);
	}

	const initials = `${(entry.first_name || '')[0] || ''}${(entry.last_name || '')[0] || ''}`.toUpperCase();

	return (
		<div style={rankingsAvatarWrapperStyle}>
			<div
				className={`${playerAvatarImgClassName} flex items-center justify-center text-xs font-semibold text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-800`}
				style={rankingsAvatarImgStyle}
				aria-hidden="true"
			>
				{initials || '?'}
			</div>
		</div>
	);
}

function ShowAvatarsToggle({ isChecked, onChange }) {
	return (
		<div className="flex items-center mr-4 my-3 md:my-4 text-gray-800 dark:text-dark-text">
			<span className="mr-2 text-sm md:text-base text-gray-800 dark:text-dark-text">
				Avatars
			</span>
			<input
				type="checkbox"
				checked={isChecked}
				onChange={(event) => onChange(event.target.checked)}
				className="hidden"
				id="showAvatarsCheckbox"
			/>
			<label htmlFor="showAvatarsCheckbox" className="cursor-pointer select-none relative">
				<div
					className={`w-8 h-4 md:w-12 md:h-6 rounded-full shadow-inner ${isChecked
						? 'bg-gray-800'
						: 'bg-gray-300'}`}
				/>
				<div
					className={`dot absolute top-0 left-0 w-4 h-4 md:w-6 md:h-6 bg-white rounded-full shadow transition transform ${isChecked
						? 'translate-x-full border border-gray-500'
						: 'border border-gray-300'}`}
				/>
			</label>
		</div>
	);
}

function PlatformDropdown({ value, onChange, disabled }) {
	return (
		<div className="flex items-center mr-2 md:mr-4 my-2 lg:my-0 text-gray-800 dark:text-dark-text">
			<label
				htmlFor="rankingsPlatformDropdown"
				className="mr-2 text-sm md:text-base text-gray-800 dark:text-dark-text whitespace-nowrap"
			>
				Platform
			</label>
			<select
				id="rankingsPlatformDropdown"
				value={value}
				onChange={(event) => onChange(event.target.value)}
				disabled={disabled}
				className="bg-white dark:bg-dark-card p-2 border border-gray-300 dark:border-dark-border rounded-md text-xs sm:text-base text-gray-800 dark:text-dark-text disabled:opacity-50"
			>
				{PLATFORM_OPTIONS.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</div>
	);
}

function ShowWeeks15To17Toggle({ isChecked, onChange }) {
	return (
		<div className="flex items-center mr-4 my-3 md:my-4 text-gray-800 dark:text-dark-text">
			<span className="mr-2 text-sm md:text-base text-gray-800 dark:text-dark-text whitespace-nowrap">
				Weeks 15–17
			</span>
			<input
				type="checkbox"
				checked={isChecked}
				onChange={(event) => onChange(event.target.checked)}
				className="hidden"
				id="rankingsShowWeeks15To17Checkbox"
			/>
			<label
				htmlFor="rankingsShowWeeks15To17Checkbox"
				className="cursor-pointer select-none relative"
			>
				<div
					className={`w-8 h-4 md:w-12 md:h-6 rounded-full shadow-inner ${
						isChecked ? 'bg-gray-800' : 'bg-gray-300'
					}`}
				/>
				<div
					className={`dot absolute top-0 left-0 w-4 h-4 md:w-6 md:h-6 bg-white rounded-full shadow transition transform ${
						isChecked
							? 'translate-x-full border border-gray-500'
							: 'border border-gray-300'
					}`}
				/>
			</label>
		</div>
	);
}

function OpponentLogo({ team, sizeClassName = 'h-7 w-7', size = TEAM_LOGO_CACHE_SIZE }) {
	const imageUrl = team?.image_url;
	const [src, setSrc] = useState(() => getCachedTeamLogoSrc(imageUrl, size));

	useEffect(() => {
		if (!imageUrl) {
			setSrc(null);
			return undefined;
		}

		prefetchTeamLogos([imageUrl], size);
		setSrc(getCachedTeamLogoSrc(imageUrl, size));

		return subscribeTeamLogoCache(() => {
			setSrc(getCachedTeamLogoSrc(imageUrl, size));
		});
	}, [imageUrl, size]);

	if (!src) {
		return <div className={`flex-shrink-0 ${sizeClassName}`} aria-hidden="true" />;
	}

	return (
		<img
			src={src}
			alt={`${team.abbreviation || team.name} logo`}
			className={`flex-shrink-0 object-contain ${sizeClassName}`}
			onError={(event) => {
				event.currentTarget.style.display = 'none';
			}}
		/>
	);
}

function FavorabilityDot({ favorability }) {
	if (!favorability || !FAVORABILITY[favorability]) {
		return (
			<span className="text-xs font-semibold text-gray-400 dark:text-gray-500">—</span>
		);
	}

	const meta = FAVORABILITY[favorability];
	return (
		<span
			className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dotClass}`}
			title={meta.label}
			aria-label={meta.label}
		/>
	);
}

function MatchupCell({ matchup, opponentTeam, hasSchedule, favorability }) {
	if (!hasSchedule) {
		return (
			<span className="text-xs font-semibold text-gray-400 dark:text-gray-500">—</span>
		);
	}

	if (!matchup) {
		return (
			<span className="text-xs font-semibold text-gray-400 dark:text-gray-500">BYE</span>
		);
	}

	const venueLabel = matchup.is_home_game ? 'vs' : '@';

	return (
		<div className="flex flex-col items-center justify-center gap-1">
			<span className="text-2xs font-bold uppercase text-gray-500 dark:text-gray-400">
				{venueLabel}
			</span>
			<OpponentLogo team={opponentTeam} />
			<FavorabilityDot favorability={favorability} />
		</div>
	);
}

function RankingsTableRow({
	entry,
	isEditing,
	isDragging,
	isDropTarget,
	showAvatars,
	showPlayoffWeeks,
	scheduleTeam,
	teamsById,
	favorabilities,
	onDragStart,
	onDragOver,
	onDrop,
	onDragEnd,
	onNotesChange
}) {
	const rowClassName = [
		TABLE_ROW_BASE_CLASS,
		isEditing ? 'cursor-move' : '',
		isDragging ? 'opacity-50 bg-blue-50 dark:bg-gray-800' : '',
		isDropTarget ? 'bg-blue-100 dark:bg-gray-700' : ''
	]
		.filter(Boolean)
		.join(' ');

	return (
		<tr
			className={rowClassName}
			draggable={isEditing}
			onDragStart={onDragStart}
			onDragOver={onDragOver}
			onDrop={onDrop}
			onDragEnd={onDragEnd}
		>
			<td className={TABLE_BODY_CELL_CLASS}>
				{isEditing && (
					<span className="mr-2 text-gray-400 dark:text-gray-500" aria-hidden="true">
						☰
					</span>
				)}
				{entry.rank}
			</td>
			<td className={TABLE_BODY_CELL_CLASS}>
				<div className="flex items-center">
					{showAvatars && <PlayerAvatar entry={entry} />}
					{entry.player_id ? (
						<a
							href={playerProfilePath(entry)}
							target="_blank"
							rel="noopener noreferrer"
							className="text-blue-500 hover:underline"
						>
							{entry.full_name}
						</a>
					) : (
						entry.full_name
					)}
				</div>
			</td>
			<td className={TABLE_BODY_CELL_CLASS}>{entry.position}</td>
			<td className={`${TABLE_BODY_CELL_CLASS} hidden sm:table-cell`}>
				{entry.team_name || '—'}
			</td>
			<td className={`${TABLE_BODY_CELL_CLASS} hidden md:table-cell`}>
				{entry.adp != null ? entry.adp.toFixed(1) : '—'}
			</td>
			<td className={`${TABLE_BODY_CELL_CLASS} hidden lg:table-cell`}>
				{entry.projected_points != null ? entry.projected_points.toFixed(1) : '—'}
			</td>
			<td className={`${TABLE_BODY_CELL_CLASS} hidden lg:table-cell`}>
				{entry.position_rank || '—'}
			</td>
			{isEditing && (
				<td className={`${TABLE_BODY_CELL_CLASS} pr-4`} style={{ minWidth: '220px' }}>
					<textarea
						value={entry.notes || ''}
						onChange={(event) => onNotesChange(entry.id, event.target.value)}
						onMouseDown={(event) => event.stopPropagation()}
						onDragStart={(event) => event.preventDefault()}
						rows={2}
						placeholder="Why this rank?"
						className="w-full resize-y rounded-md border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1.5 text-xs md:text-sm text-gray-800 dark:text-dark-text focus:outline-none focus:ring-1 focus:ring-blue-500"
						aria-label={`Notes for ${entry.full_name}`}
					/>
				</td>
			)}
			{showPlayoffWeeks &&
				PLAYOFF_WEEKS.map((week) => {
					const matchup = getWeekMatchup(scheduleTeam, week);
					const opponentTeam = matchup ? teamsById[matchup.opponent_id] : null;

					return (
						<td key={`week-${entry.id}-${week}`} className={WEEK_BODY_CELL_CLASS}>
							<MatchupCell
								matchup={matchup}
								opponentTeam={opponentTeam}
								hasSchedule={Boolean(scheduleTeam)}
								favorability={getWeekFavorability(favorabilities, week)}
							/>
						</td>
					);
				})}
		</tr>
	);
}

export default function PlayerRankingsPage({
	get_player_rankings_endpoint,
	import_player_rankings_endpoint,
	reorder_player_rankings_endpoint,
	export_player_rankings_endpoint,
	get_championship_weeks_endpoint,
	get_eliminator_rankings_favorabilities_endpoint
}) {
	const { user, authenticated } = useAuth();

	const [season] = useState(DEFAULT_SEASON);
	const [scoringFormat] = useState(DEFAULT_SCORING_FORMAT);
	const [platform, setPlatform] = useState(DEFAULT_PLATFORM);
	const [rankingSet, setRankingSet] = useState(null);
	const [entries, setEntries] = useState([]);
	const [draftEntries, setDraftEntries] = useState([]);
	const [teams, setTeams] = useState([]);
	const [favorabilityLookup, setFavorabilityLookup] = useState({});
	const [totalCount, setTotalCount] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [isEditing, setIsEditing] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [draggedId, setDraggedId] = useState(null);
	const [dropTargetId, setDropTargetId] = useState(null);
	const [filter, setFilter] = useState({ position: [], team: '', isRookie: false });
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [page, setPage] = useState(1);
	const [perPage, setPerPage] = useState(25);
	const [showAvatars, setShowAvatars] = useState(true);
	const [showWeeks15To17, setShowWeeks15To17] = useState(false);
	const fileInputRef = useRef(null);
	const scheduleLoadedRef = useRef(false);

	const canEditRankings = authenticated && user?.email === PROJECTION_EDITOR_EMAIL;
	const isEditingMode = canEditRankings && isEditing;

	useEffect(() => {
		const timer = window.setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);
		return () => window.clearTimeout(timer);
	}, [search]);

	const browseQueryParams = useMemo(() => {
		const params = {
			season,
			scoring_format: scoringFormat,
			platform,
			page,
			per_page: perPage
		};

		if (filter.position?.length) {
			params.position = filter.position.join(',');
		}
		if (filter.team) {
			params.team = filter.team;
		}
		if (filter.isRookie) {
			params.is_rookie = true;
		}
		if (debouncedSearch.trim()) {
			params.search = debouncedSearch.trim();
		}

		return params;
	}, [season, scoringFormat, platform, page, perPage, filter, debouncedSearch]);

	const fetchBrowseRankings = useCallback(async () => {
		if (!get_player_rankings_endpoint || isEditingMode) return;

		setIsLoading(true);
		setError('');

		try {
			const response = await fetch(
				buildEndpoint(get_player_rankings_endpoint, browseQueryParams),
				{
					credentials: 'same-origin',
					headers: { Accept: 'application/json' }
				}
			);
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				throw new Error(payload.error || 'Failed to load rankings.');
			}

			const data = payload.data;
			setRankingSet(data);
			setEntries(data?.entries || []);
			setTotalCount(data?.pagination?.total_count ?? data?.entries_count ?? 0);
			setTotalPages(
				Math.max(1, data?.pagination?.total_pages ?? 1)
			);
		} catch (fetchError) {
			setError(fetchError.message);
			setRankingSet(null);
			setEntries([]);
			setTotalCount(0);
			setTotalPages(1);
		} finally {
			setIsLoading(false);
		}
	}, [get_player_rankings_endpoint, browseQueryParams, isEditingMode]);

	const fetchScheduleIfNeeded = useCallback(async () => {
		if (!get_championship_weeks_endpoint || scheduleLoadedRef.current) {
			return teams;
		}

		const response = await fetch(get_championship_weeks_endpoint, {
			credentials: 'same-origin',
			headers: { Accept: 'application/json' }
		});
		const payload = await response.json();

		if (!response.ok || !payload.success) {
			throw new Error(payload.error || 'Failed to load championship schedule.');
		}

		const nextTeams = payload.data?.teams || [];
		setTeams(nextTeams);
		prefetchTeamLogos(
			nextTeams.map((team) => team.image_url),
			TEAM_LOGO_CACHE_SIZE
		);
		scheduleLoadedRef.current = true;
		return nextTeams;
	}, [get_championship_weeks_endpoint, teams]);

	const fetchFavorabilitiesForEntries = useCallback(
		async (sourceEntries) => {
			if (!get_eliminator_rankings_favorabilities_endpoint) {
				setFavorabilityLookup({});
				return;
			}

			const playerIds = [
				...new Set(
					(sourceEntries || [])
						.map((entry) => entry.player_id)
						.filter((id) => id != null)
				)
			];

			const response = await fetch(
				buildEndpoint(get_eliminator_rankings_favorabilities_endpoint, {
					season,
					weeks: PLAYOFF_WEEKS.join(','),
					player_ids: playerIds.join(',')
				}),
				{
					credentials: 'same-origin',
					headers: { Accept: 'application/json' }
				}
			);
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				setFavorabilityLookup({});
				return;
			}

			setFavorabilityLookup(
				buildFavorabilityLookupFromMap(payload.data?.by_player || {})
			);
		},
		[get_eliminator_rankings_favorabilities_endpoint, season]
	);

	useEffect(() => {
		fetchBrowseRankings();
	}, [fetchBrowseRankings]);

	useEffect(() => {
		if (!canEditRankings) {
			setIsEditing(false);
			setDraftEntries([]);
			setDraggedId(null);
			setDropTargetId(null);
		}
	}, [canEditRankings]);

	useEffect(() => {
		if (!showWeeks15To17) return undefined;

		let cancelled = false;

		(async () => {
			try {
				await fetchScheduleIfNeeded();
				if (cancelled) return;
				await fetchFavorabilitiesForEntries(
					isEditingMode ? draftEntries : entries
				);
			} catch (scheduleError) {
				if (!cancelled) {
					setError(scheduleError.message);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		showWeeks15To17,
		entries,
		draftEntries,
		isEditingMode,
		fetchScheduleIfNeeded,
		fetchFavorabilitiesForEntries
	]);

	const teamLookup = useMemo(() => buildTeamLookup(teams), [teams]);

	const teamsById = useMemo(() => {
		return teams.reduce((lookup, team) => {
			lookup[team.id] = team;
			return lookup;
		}, {});
	}, [teams]);

	const displayEntries = useMemo(() => {
		if (!isEditingMode) return entries;
		return filterRankingEntries(draftEntries, filter, search);
	}, [isEditingMode, entries, draftEntries, filter, search]);

	useEffect(() => {
		if (isEditingMode) return;
		setPage(1);
	}, [filter, debouncedSearch, perPage, platform, isEditingMode]);

	useEffect(() => {
		if (isEditingMode) return;
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages, isEditingMode]);

	const handleFilterChange = (filterKey, value) => {
		setFilter((prevFilter) => ({ ...prevFilter, [filterKey]: value }));
	};

	const handleSearchInputChange = (event) => {
		setSearch(event.target.value);
	};

	const handleClearFilters = () => {
		setFilter({ position: [], team: '', isRookie: false });
		setSearch('');
		setDebouncedSearch('');
		setPage(1);
	};

	const handlePerPageChange = (newPerPage) => {
		setPerPage(newPerPage);
		setPage(1);
	};

	const handlePlatformChange = (nextPlatform) => {
		if (nextPlatform === platform || isEditingMode) return;

		setPlatform(nextPlatform);
		setPage(1);
		setRankingSet(null);
		setEntries([]);
		setIsLoading(true);
		setError('');
		setSuccessMessage('');
	};

	const platformLabel =
		PLATFORM_OPTIONS.find((option) => option.value === platform)?.label ||
		'Underdog';

	const handleStartEdit = async () => {
		if (!canEditRankings || !get_player_rankings_endpoint) return;

		setIsLoading(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(
				buildEndpoint(get_player_rankings_endpoint, {
					season,
					scoring_format: scoringFormat,
					platform,
					mode: 'edit'
				}),
				{
					credentials: 'same-origin',
					headers: { Accept: 'application/json' }
				}
			);
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				throw new Error(payload.error || 'Failed to load rankings for editing.');
			}

			const nextEntries = payload.data?.entries || [];
			setRankingSet(payload.data);
			setDraftEntries(nextEntries);
			setIsEditing(true);

			if (showWeeks15To17) {
				await fetchScheduleIfNeeded();
				await fetchFavorabilitiesForEntries(nextEntries);
			}
		} catch (editError) {
			setError(editError.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancelEdit = () => {
		setDraftEntries([]);
		setIsEditing(false);
		setDraggedId(null);
		setDropTargetId(null);
	};

	const handleSave = async () => {
		if (!canEditRankings || !reorder_player_rankings_endpoint) return;

		setIsSaving(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(
				buildEndpoint(reorder_player_rankings_endpoint, {
					season,
					scoring_format: scoringFormat,
					platform
				}),
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': getCsrfToken()
					},
					body: JSON.stringify({
						ordered_entry_ids: draftEntries.map((entry) => entry.id),
						entry_notes: draftEntries.reduce((memo, entry) => {
							memo[entry.id] = entry.notes || '';
							return memo;
						}, {})
					})
				}
			);
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				throw new Error(payload.error || 'Failed to save rankings.');
			}

			setDraftEntries([]);
			setIsEditing(false);
			setSuccessMessage('Rankings saved.');
		} catch (saveError) {
			setError(saveError.message);
		} finally {
			setIsSaving(false);
		}
	};

	const handleExport = async () => {
		if (
			!canEditRankings ||
			!export_player_rankings_endpoint ||
			(isEditingMode ? draftEntries.length === 0 : totalCount === 0)
		) {
			return;
		}

		setIsExporting(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(
				buildEndpoint(export_player_rankings_endpoint, {
					season,
					scoring_format: scoringFormat,
					platform
				}),
				{ credentials: 'same-origin' }
			);

			if (!response.ok) {
				let message = 'Failed to export rankings.';
				try {
					const payload = await response.json();
					message = payload.error || message;
				} catch (jsonError) {
					// response body is not JSON (e.g. CSV error page)
				}
				throw new Error(message);
			}

			const blob = await response.blob();
			const disposition = response.headers.get('Content-Disposition');
			const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
			const filename =
				filenameMatch?.[1] ||
				`player-rankings-${season}-${scoringFormat}-${platform}.csv`;

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);

			setSuccessMessage('Rankings exported.');
		} catch (exportError) {
			setError(exportError.message);
		} finally {
			setIsExporting(false);
		}
	};

	const handleFileChange = async (event) => {
		if (!canEditRankings) return;

		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file || !import_player_rankings_endpoint) return;

		if (!file.name.toLowerCase().endsWith('.csv')) {
			setError('Please upload a CSV file.');
			return;
		}

		setIsUploading(true);
		setError('');
		setSuccessMessage('');

		try {
			const csvContent = await file.text();
			const response = await fetch(import_player_rankings_endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': getCsrfToken()
				},
				body: JSON.stringify({
					season,
					scoring_format: scoringFormat,
					platform,
					csv_content: csvContent
				})
			});
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				throw new Error(payload.error || 'Failed to import rankings.');
			}

			setRankingSet(payload.data);
			setDraftEntries([]);
			setIsEditing(false);
			const count = payload.data.entries_count;
			const truncatedNote =
				payload.data.truncated && payload.data.total_in_csv
					? ` (first ${count} of ${payload.data.total_in_csv} in file)`
					: '';
			setSuccessMessage(`Imported ${count} players${truncatedNote}.`);
		} catch (uploadError) {
			setError(uploadError.message);
		} finally {
			setIsUploading(false);
		}
	};

	const handleDragStart = (entryId) => (event) => {
		setDraggedId(entryId);
		event.dataTransfer.effectAllowed = 'move';
		event.dataTransfer.setData('text/plain', String(entryId));
	};

	const handleDragOver = (entryId) => (event) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
		if (entryId !== draggedId) {
			setDropTargetId(entryId);
		}
	};

	const handleDrop = (entryId) => (event) => {
		event.preventDefault();
		if (!draggedId || draggedId === entryId) return;

		setDraftEntries((current) => reorderEntries(current, draggedId, entryId));
		setDraggedId(null);
		setDropTargetId(null);
	};

	const handleDragEnd = () => {
		setDraggedId(null);
		setDropTargetId(null);
	};

	const handleNotesChange = (entryId, notes) => {
		setDraftEntries((current) =>
			current.map((entry) =>
				entry.id === entryId
					? {
							...entry,
							notes
						}
					: entry
			)
		);
	};

	if (!get_player_rankings_endpoint) {
		return <div>Error: Missing player rankings endpoint.</div>;
	}

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 lg:mb-6">
				<div>
					<PageTitle title="Player Rankings" />
					<p className="text-sm text-gray-600 dark:text-gray-400 -mt-2">
						{rankingSet?.name ||
							`${season} Season | Best Ball | ${platformLabel} | 0.5 PPR`}
					</p>
				</div>

				<div className="flex flex-wrap items-center gap-2">
					<PlatformDropdown
						value={platform}
						onChange={handlePlatformChange}
						disabled={isEditingMode}
					/>
					{canEditRankings && (
						<div className="flex flex-wrap items-center gap-2">
						{!isEditingMode ? (
							<button
								type="button"
								className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
								onClick={handleStartEdit}
								disabled={isUploading}
							>
								Edit
							</button>
						) : (
							<React.Fragment>
								<input
									ref={fileInputRef}
									type="file"
									accept=".csv,text/csv"
									className="hidden"
									onChange={handleFileChange}
								/>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleExport}
									disabled={
										isExporting ||
										isUploading ||
										isSaving ||
										(isEditingMode ? draftEntries.length === 0 : totalCount === 0)
									}
								>
									{isExporting ? 'Exporting…' : 'Export CSV'}
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={() => fileInputRef.current?.click()}
									disabled={isUploading || isSaving}
								>
									{isUploading ? 'Uploading…' : 'Upload CSV'}
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleCancelEdit}
									disabled={isSaving || isUploading}
								>
									Cancel
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
									onClick={handleSave}
									disabled={isSaving || isUploading || draftEntries.length === 0}
								>
									{isSaving ? 'Saving…' : 'Save'}
								</button>
							</React.Fragment>
						)}
						</div>
					)}
				</div>
			</div>

			{error && (
				<div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-300">
					{error}
				</div>
			)}

			{successMessage && (
				<div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-300">
					{successMessage}
				</div>
			)}

			{isEditingMode && (
				<div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-200">
					Drag rows to reorder players, then click Save.
				</div>
			)}

			<div className="relative z-40 flex flex-col lg:flex-row lg:mb-4 justify-between">
				<FilterByPosition
					filter={filter}
					onChange={handleFilterChange}
					hideLabel
					positionButtons={POSITION_BUTTONS}
				/>
				<FilterByTeamMultiSelect
					filter={filter}
					onChange={handleFilterChange}
					hideLabel
				/>
				<div className="hidden lg:contents">
					<FilterBySearchInput
						value={search}
						placeholder="Search for a player"
						onChange={handleSearchInputChange}
						onEnter={() => {}}
					/>
					<FilterByIsRookie filter={filter} onChange={handleFilterChange} />
					<ShowAvatarsToggle
						isChecked={showAvatars}
						onChange={setShowAvatars}
					/>
					<ShowWeeks15To17Toggle
						isChecked={showWeeks15To17}
						onChange={setShowWeeks15To17}
					/>
				</div>
				<div className="lg:hidden contents">
					<div className="flex items-start space-x-2 sm:space-x-4 w-full lg:w-auto">
						<FilterBySearchInput
							value={search}
							placeholder="Search for a player"
							onChange={handleSearchInputChange}
							onEnter={() => {}}
						/>
						<FilterByIsRookie filter={filter} onChange={handleFilterChange} />
						<ShowAvatarsToggle
							isChecked={showAvatars}
							onChange={setShowAvatars}
						/>
						<ShowWeeks15To17Toggle
							isChecked={showWeeks15To17}
							onChange={setShowWeeks15To17}
						/>
					</div>
				</div>
				<div className="hidden lg:contents">
					<ClearFiltersButton onClick={handleClearFilters} />
				</div>
			</div>

			<div className={`relative z-0 ${TABLE_WRAPPER_CLASS}`}>
				{isLoading ? (
					<div className="p-8 flex justify-center">
						<LoadingSpinner />
					</div>
				) : !rankingSet && !isEditingMode ? (
					<div className="p-8 text-center text-gray-600 dark:text-gray-400">
						{canEditRankings
							? `No ${platformLabel} rankings yet. Edit and upload a CSV to get started.`
							: `${platformLabel} rankings are not available yet.`}
					</div>
				) : displayEntries.length === 0 ? (
					<div className="p-8 text-center text-gray-600 dark:text-gray-400">
						{isEditingMode
							? 'No players match your filters.'
							: totalCount === 0
								? 'No players match your filters.'
								: 'No players on this page.'}
					</div>
				) : (
					<table className="w-full">
						<thead className={TABLE_HEAD_CLASS}>
							<tr>
								<th className={TABLE_HEAD_CELL_CLASS}>Rank</th>
								<th className={TABLE_HEAD_CELL_CLASS}>Player</th>
								<th className={TABLE_HEAD_CELL_CLASS}>Pos</th>
								<th className={`${TABLE_HEAD_CELL_CLASS} hidden sm:table-cell`}>Team</th>
								<th className={`${TABLE_HEAD_CELL_CLASS} hidden md:table-cell`}>ADP</th>
								<th className={`${TABLE_HEAD_CELL_CLASS} hidden lg:table-cell`}>
									Proj Pts
								</th>
								<th className={`${TABLE_HEAD_CELL_CLASS} hidden lg:table-cell`}>
									Pos Rank
								</th>
								{isEditingMode && (
									<th className={`${TABLE_HEAD_CELL_CLASS} pr-4`}>Notes/Rationale</th>
								)}
								{showWeeks15To17 &&
									PLAYOFF_WEEKS.map((week) => (
										<th key={`week-head-${week}`} className={WEEK_HEAD_CELL_CLASS}>
											Week {week}
										</th>
									))}
							</tr>
						</thead>
						<tbody>
							{displayEntries.map((entry) => (
									<RankingsTableRow
										key={entry.id}
										entry={entry}
										isEditing={isEditingMode}
										showAvatars={showAvatars}
										showPlayoffWeeks={showWeeks15To17}
										scheduleTeam={
											teamLookup[normalizeTeamKey(entry.team_name)] || null
										}
										teamsById={teamsById}
										favorabilities={lookupEntryFavorabilities(
											favorabilityLookup,
											entry
										)}
										isDragging={draggedId === entry.id}
										isDropTarget={dropTargetId === entry.id}
										onDragStart={handleDragStart(entry.id)}
										onDragOver={handleDragOver(entry.id)}
										onDrop={handleDrop(entry.id)}
										onDragEnd={handleDragEnd}
										onNotesChange={handleNotesChange}
									/>
							))}
						</tbody>
					</table>
				)}
			</div>

			{(isEditingMode ? draftEntries.length > 0 : totalCount > 0) && (
				<React.Fragment>
					<p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
						{isEditingMode ? (
							`Showing ${displayEntries.length} of ${draftEntries.length} players for reordering`
						) : (
							`Showing ${displayEntries.length} of ${totalCount} players`
						)}
					</p>

					{!isEditingMode && (
						<div className="flex flex-col items-center gap-4 mt-8 relative">
							<div className="flex items-center justify-between w-full">
								<div className="hidden lg:contents">
									<div className="flex justify-center w-full">
										<PaginationTracker
											currentPage={page}
											setPage={setPage}
											totalPages={totalPages}
										/>
									</div>
								</div>
								<div className="contents lg:hidden">
									<div className="flex justify-center w-full">
										<PaginationTrackerMobile
											currentPage={page}
											setPage={setPage}
											totalPages={totalPages}
										/>
									</div>
								</div>
								<div className="hidden lg:contents">
									<div className="absolute right-0">
										<PerPageDropdown
											perPage={perPage}
											setPerPage={handlePerPageChange}
										/>
									</div>
								</div>
							</div>
						</div>
					)}
				</React.Fragment>
			)}
		</div>
	);
}
